import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Edit, Trash2, Eye, MoreVertical } from "lucide-react";
import toast from "react-hot-toast";

const PROJECTS_URL = "https://api.livestockify.com/api/urjabio/projects";

const AllProjects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(null);

  const getToken = () => localStorage.getItem("token");

  /* ---------------- FETCH PROJECTS ---------------- */
  
  useEffect(() => {
    const load = async () => {
      try {
        const token = getToken();
        const res = await axios.get(PROJECTS_URL, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setProjects(data);
      } catch {
        toast.error("Failed to load projects");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  /* ---------------- HELPERS ---------------- */

  const toggleMenu = (id) => {
    setMenuOpen((prev) => (prev === id ? null : id));
  };

  const handleDelete = async (project) => {
    try {
      await axios.delete(`${PROJECTS_URL}/${project.projectUuid}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success("Project deleted");
      setProjects((prev) =>
        prev.filter((p) => p.projectUuid !== project.projectUuid),
      );
    } catch {
      toast.error("Delete failed");
    }
  };

  /* ---------------- SEARCH ---------------- */

  const filteredProjects = projects.filter((p) => {
    const deviceId = p.devices?.[0]?.deviceId || "";
    return (
      p.projectName?.toLowerCase().includes(search.toLowerCase()) ||
      deviceId.toLowerCase().includes(search.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        Loading projects...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <button
          onClick={() => navigate("/urjabio/admin/dashboard")}
          className="text-sm text-gray-600 hover:text-[#34C759]"
        >
          ← Go Back
        </button>

        <h2 className="text-2xl font-semibold text-center">All Projects</h2>

        <div className="flex gap-3 w-full sm:w-auto">
          <button
            onClick={() => navigate("/urjabio/admin/add-project")}
            className="bg-[#34C759] text-white px-5 py-2 rounded-md w-full sm:w-auto"
          >
            Add Project
          </button>

          <div className="relative w-full sm:w-64">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="w-full border rounded-md px-4 py-2"
            />
            <Search
              className="absolute right-3 top-2.5 text-gray-400"
              size={18}
            />
          </div>
        </div>
      </div>

      {/* TABLE HEADER (Desktop) */}
      <div className="hidden md:grid grid-cols-7 bg-gray-100 text-sm font-semibold text-gray-700 border border-gray-300 rounded-t-md">
        <div className="px-4 py-3 border-r text-center">Project Name</div>
        <div className="px-4 py-3 border-r text-center">Location</div>
        <div className="px-4 py-3 border-r text-center">Device ID</div>
        <div className="px-4 py-3 border-r text-center">Last Reading</div>
        <div className="px-4 py-3 border-r text-center">Edit</div>
        <div className="px-4 py-3 border-r text-center">Delete</div>
        <div className="px-4 py-3 text-center">View</div>
      </div>

      {/* ROWS */}
      <div className="divide-y divide-gray-300 border border-gray-300 md:border-t-0 rounded-b-md bg-white">
        {filteredProjects.map((p) => {
          const deviceId = p.devices?.[0]?.deviceId || "-";

          return (
            <div
              key={p.projectUuid}
              className="grid grid-cols-1 md:grid-cols-7 gap-4 md:gap-5 py-4 px-4 md:px-0 items-center"
            >
              {/* MOBILE CARD HEADER */}
              <div className="md:hidden flex justify-between items-start">
                <div>
                  <p className="font-semibold">{p.projectName}</p>
                  <p className="text-sm text-gray-500">{p.projectLocation}</p>

                  <p className="text-xs text-gray-500 mt-1">
                    Devices: {p.devices?.length ?? 0}
                  </p>
                </div>

                <div className="relative">
                  <button onClick={() => toggleMenu(p.projectUuid)}>
                    <MoreVertical size={18} />
                  </button>

                  {menuOpen === p.projectUuid && (
                    <div className="absolute right-0 mt-2 w-32 bg-white border rounded shadow">
                      <button
                        onClick={() =>
                          navigate("/urjabio/admin/add-project", {
                            state: { editData: p },
                          })
                        }
                        className="w-full px-3 py-2 flex gap-2 text-sm hover:bg-gray-100"
                      >
                        <Edit size={14} /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(p)}
                        className="w-full px-3 py-2 flex gap-2 text-sm text-red-500 hover:bg-gray-100"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* DESKTOP CELLS */}
              <div className="hidden md:block text-center">{p.projectName}</div>
              <div className="hidden md:block text-center">
                {p.projectLocation || "-"}
              </div>
              <div className="hidden md:block text-center text-sm break-all">
                {deviceId}
              </div>
              <div className="hidden md:block text-center text-sm">
                Devices: {p.devices?.length ?? 0}
              </div>

              {/* EDIT */}
              <div className="hidden md:flex justify-center">
                <button
                  onClick={() =>
                    navigate("/urjabio/admin/add-project", {
                      state: { editData: p },
                    })
                  }
                  className="text-emerald-600 flex gap-1 items-center"
                >
                  <Edit size={16} /> Edit
                </button>
              </div>

              {/* DELETE */}
              <div className="hidden md:flex justify-center">
                <button
                  onClick={() => handleDelete(p)}
                  className="text-red-500 flex gap-1 items-center"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>

              {/* VIEW */}
              <div className="flex justify-between md:justify-center items-center">
                <span className="md:hidden text-sm text-gray-500">
                  {deviceId}
                </span>
                <button
                  onClick={() =>
                    navigate(`/urjabio/admin/metrics/${p.projectUuid}`)
                  }
                  className="text-[#34C759] flex gap-1 items-center"
                >
                  <Eye size={16} /> View
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AllProjects;
